import { AppForm } from '../../../../../../../common/forms/app.form';
import { Injectable, Signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite
} from '../../../../../../../openapi';
import { InjuredPersonForm } from './injured-person-form';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  DictTownRoadItem, DictTownStreetItem,
  DictTownWithRoadsItem,
  DictTownWithStreetsItem
} from '../../../../../../../services/dictionary.common.model';
import { DictionaryCommonService } from '../../../../../../../services/dictionary.common.service';

@Injectable()
export class EventForm extends AppForm {

  public townWithStreetsList: Signal<DictTownWithStreetsItem[] | undefined> = toSignal(this.dictService.getTownWithStreetsList());

  public townWithRoadsList: Signal<DictTownWithRoadsItem[] | undefined> = toSignal(this.dictService.getTownWithRoadsList());

  public getRoadList(townName?: string | null): Array<DictTownRoadItem> {
    return this.townWithRoadsList()?.filter(v => v.name === townName).at(0)?.roads || [];
  }

  public getStreetList(townName?: string | null): Array<DictTownStreetItem> {
    return this.townWithStreetsList()?.filter(v => v.name === townName)?.at(0)?.streets || [];
  }

  constructor(private dictService: DictionaryCommonService) {
    super({
      provideDateRangeDescription: new FormControl(false),
      eventLocation: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      eventDescription: new FormControl(null, [Validators.required]),
      geometry: new FormControl(null),

      provideRoadNumberAndName: new FormControl(true),
      city: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),

      description: new FormControl(null, [Validators.maxLength(5000), Validators.required]),
      injuredDataSameAsApplicant: new FormControl(false, { nonNullable: true }),
      injuredWas: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      damageType: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      servicesNotifiedAboutEvent: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      additionalInformation: new FormControl(null, [Validators.maxLength(5000)]),
      witnessStatement: new FormControl(null, [Validators.maxLength(5000)])
    });
    this.provideDateRangeDescriptionChange(false);
    this.injuredDataSameAsApplicantChange(false);
    this.provideRoadNumberAndNameChange(true);
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
    this.roadNumber?.setValue('');
    this.roadNumber?.markAsUntouched();
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));
      this.removeControl('street');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');
      this.addControl('street', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public roadNumberChange(roadNumber: string): void {
    const roadName = this.getRoadList(this.city.value).filter(v => v.roadNumber === roadNumber).at(0)?.roadName || '';
    this.roadName?.setValue(roadName);
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public get servicesNotifiedAboutEventDesc(): FormControl | null {
    return this.get('servicesNotifiedAboutEventDesc') as FormControl | null;
  }

  public get damageTypeDesc(): FormControl | null {
    return this.get('damageTypeDesc') as FormControl | null;
  }

  public damageTypeChange(v: string): void {
    if (v === 'Inne') {
      this.addControl('damageTypeDesc', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('damageTypeDesc');
    }
  }

  public servicesNotifiedAboutEventChange(v: string): void {
    if (v === 'Inne służby') {
      this.addControl('servicesNotifiedAboutEventDesc', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('servicesNotifiedAboutEventDesc');
    }
  }

  public get injuredWasDesc(): FormControl | null {
    return this.get('injuredWasDesc') as FormControl | null;
  }

  public injuredWasChange(v: string): void {
    if (v === 'Inne') {
      this.addControl('injuredWasDesc', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('injuredWasDesc');
    }
  }

  public get injuredPersonForm(): InjuredPersonForm | null {
    return this.get('injuredPerson') as InjuredPersonForm | null;
  }

  public injuredDataSameAsApplicantChange(v: boolean): void {
    if (v) {
      this.removeControl('injuredPerson');
    } else {
      this.addControl('injuredPerson', new InjuredPersonForm());
    }
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public get eventStartDate(): FormControl | null {
    return this.get('eventStartDate') as FormControl | null;
  }

  public get eventStartTime(): FormControl | null {
    return this.get('eventStartTime') as FormControl | null;
  }

  public get dateRangeDescription(): FormControl | null {
    return this.get('dateRangeDescription') as FormControl | null;
  }

  public provideDateRangeDescriptionChange(v: boolean): void {
    if (v) {
      this.removeControl('eventStartDate');
      this.removeControl('eventStartTime');
      this.addControl('dateRangeDescription', new FormControl('', [Validators.required, Validators.maxLength(1000)]));
    } else {
      this.addControl('eventStartDate', new FormControl(null, Validators.required));
      this.addControl('eventStartTime', new FormControl(null, Validators.required));
      this.removeControl('dateRangeDescription');
    }
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      ...this.value,
      dateRangeDescription: this.value?.dateRangeDescription || null,
      injuredWasDesc: this.value?.injuredWasDesc || null,
      servicesNotifiedAboutEventDesc: this.value?.servicesNotifiedAboutEventDesc || null,
      street: this.value?.street || null,
      roadName: this.roadName?.value || null,
      roadNumber: this.value?.roadNumber || null,
      witnessStatement: this.value?.witnessStatement || null,
      damageTypeDesc: this.value?.damageTypeDesc || null,
      additionalInformation: this.value?.additionalInformation || null,
      ...this.injuredPersonForm?.toWriteModel()
    };
  }

  public override patchFormValues(value: ApplicationAppEntityApplicationRead): void {
    this.provideRoadNumberAndNameChange(!!value.provideRoadNumberAndName);

    super.patchFormValues({
      ...value,
      provideRoadNumberAndName: !!value?.provideRoadNumberAndName
    });

    this.provideDateRangeDescriptionChange(!!value.provideDateRangeDescription);
    this.eventStartDate?.setValue(value?.eventStartDate || null);
    this.eventStartTime?.setValue(value?.eventStartTime || null);
    this.dateRangeDescription?.setValue(value?.dateRangeDescription || '');

    this.injuredDataSameAsApplicantChange(!!value?.injuredDataSameAsApplicant);
    this.injuredPersonForm?.patchFormValues(value);

    this.injuredWasChange(value?.injuredWas || '');
    this.injuredWasDesc?.setValue(value?.injuredWasDesc || '');

    this.servicesNotifiedAboutEventChange(value?.servicesNotifiedAboutEvent || '');
    this.servicesNotifiedAboutEventDesc?.setValue(value?.servicesNotifiedAboutEventDesc || '');
  }
}
